import { IImageListItem } from '@types';
import { SecureImage, Text, CollectionSelectModal, CollectionUpsertModal } from '..';
import { colorSet } from '@theme';
import { styled } from '@mui/material';
import { useLocation } from 'react-router';
import { PathnameEnum, ToastMessageEnum } from '@constant';
import { downloadImage } from '@utils';
import { useCollection, useToast } from '@hooks';
import {
  BookmarkGray600Icon,
  BookmarkWhiteIcon,
  CheckGray300Icon,
  CheckWhiteIcon,
  DownloadWhiteIcon,
  SequenceIcon
} from '@assets/iconComponents';

interface IImageListItemComponentProps {
  item: IImageListItem;
  itemSize: string;
  onImageClick: (imageId: number, sequenceId?: number, s3Url?: string) => void;
  onUseButtonClick: (imageId: number, isUsed: boolean, usedCount: number, isCollected?: boolean) => void;
  createQueryStringForImageList?: () => string;
}

// pseudo selector를 사용하기 위해 MUI styled component 이용
const Container = styled('div')({
  '&:hover .hover-visible': {
    opacity: '1 !important', // !important 추가하여 우선순위 높임
    visibility: 'visible !important',
    pointerEvents: 'auto !important'
  },
  '&:hover .black-overlay': {
    opacity: '1 !important',
    visibility: 'visible !important'
  }
});

// 19금 이미지 hover시 오버레이
const BlackOverlay = styled('div')({
  width: '100%', // 부모 div의 너비를 채우도록 설정
  height: '100%', // 부모 div의 높이를 채우도록 설정
  borderRadius: '6px',
  position: 'absolute',
  top: 0,
  left: 0,
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)',
  opacity: 0,
  visibility: 'hidden'
});

// 사용한 이미지 오버레이
const WhiteOverlay = styled('div')({
  width: '100%', // 부모 div의 너비를 채우도록 설정
  height: '100%', // 부모 div의 높이를 채우도록 설정
  borderRadius: '6px',
  position: 'absolute',
  top: 0,
  left: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)'
});

const ButtonsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  position: 'absolute',
  bottom: 10,
  left: 10,
  right: 10
});

const UseButton = styled('div')<{ isUsed: boolean }>(({ isUsed }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 4,
  padding: '8px 9px',
  alignItems: 'center',
  borderRadius: '22px',
  opacity: isUsed ? 1 : 0,
  pointerEvents: isUsed ? 'auto' : 'none',
  backgroundColor: isUsed ? colorSet.gray600 : colorSet.white,
  border: isUsed ? 'none' : `1px solid ${colorSet.gray300}`,
  visibility: isUsed ? 'visible' : 'hidden'
}));

const BookmarkButton = styled('div')<{ isCollected: boolean }>(({ isCollected }) => ({
  width: 30,
  height: 30,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '22px',
  opacity: 0,
  pointerEvents: 'none',
  border: isCollected ? 'none' : `1px solid ${colorSet.gray300}`,
  backgroundColor: isCollected ? colorSet.gray600 : colorSet.white
}));

const DownloadButton = styled('div')({
  width: 30,
  height: 30,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '22px',
  opacity: 0,
  pointerEvents: 'none',
  backgroundColor: colorSet.primaryMain
});

export const ImageListItemComponent = (props: IImageListItemComponentProps) => {
  const { item, itemSize, onImageClick, onUseButtonClick, createQueryStringForImageList } = props;

  const {
    isCollectionSelectModalVisible,
    handleCollectionSelectModalOpen,
    handleCollectionSelectModalClose,
    isCollectionUpsertModalVisible,
    showGoBackButton,
    handleCollectionUpsertModalOpen,
    handleCollectionUpsertModalClose,
    handleGoBackToCollectionSelectModal
  } = useCollection();

  const { showToast } = useToast();

  const location = useLocation();
  const locationPathname = location.pathname;
  const isStandardOutputPage = locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE; // 스탠다드 Output 페이지인 경우 true
  const isAdultsOnlyOutputPage = locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE; // 19금 Output 페이지인 경우 true
  const isCollectionImageListPage = /^\/collections\/\d+$/.test(locationPathname); //pathname이 /collections/숫자 패턴과 일치하는지 검사
  const isUsedImageListPage = locationPathname === PathnameEnum.USED_IMAGE_LIST_PAGE;
  const showAdditionalImageInfo =
    isStandardOutputPage || isAdultsOnlyOutputPage || isCollectionImageListPage || isUsedImageListPage; //시퀀스컷 여부, 사용 버튼, 컬렉션 버튼, 다운로드 버튼을 보여줄지 여부

  return (
    <>
      <Container
        style={{
          position: 'relative',
          flex: `0 0 ${itemSize}`, // 7개 아이템을 배치하면서 간격을 고려한 너비 계산
          width: itemSize, // 7개 아이템에 맞는 너비 계산
          aspectRatio: '1', // 비율을 1:1로 설정하여 정사각형 형태 유지
          overflow: 'hidden', // 이미지가 부모 크기를 넘지 않도록 설정
          cursor: 'pointer'
        }}
        onClick={() => {
          onImageClick(item.id, item.sequence?.id, item.s3Url);
        }}
      >
        <SecureImage
          imageUrl={item.s3ThumbnailUrl ?? item.s3Url}
          imageStyle={{
            width: '100%', // 부모 div의 너비를 채우도록 설정
            height: '100%', // 부모 div의 높이를 채우도록 설정
            objectFit: 'cover', // 이미지 비율을 유지하면서 채우기
            borderRadius: '6px',
            border: `1px solid ${colorSet.gray300}`
          }}
        />

        {showAdditionalImageInfo && item.sequence ? (
          <SequenceIcon
            style={{
              width: 44,
              height: 44,
              position: 'absolute',
              top: 4,
              right: 0
            }}
          />
        ) : null}

        {showAdditionalImageInfo ? <BlackOverlay className="black-overlay" /> : null}

        {showAdditionalImageInfo && item.isUsed ? <WhiteOverlay /> : null}

        {showAdditionalImageInfo ? (
          <ButtonsContainer>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
              <UseButton
                className={item.isUsed ? '' : 'hover-visible'} // isUsed가 false일 때만 hover-visible 클래스 추가
                isUsed={item.isUsed}
                onClick={(event) => {
                  event.stopPropagation(); // 이벤트 전파 방지
                  onUseButtonClick(item.id, item.isUsed, item.usedCount, item.isCollected);
                }}
              >
                {item.isUsed ? (
                  <CheckWhiteIcon
                    style={{
                      width: 14,
                      height: 14
                    }}
                  />
                ) : (
                  <CheckGray300Icon
                    style={{
                      width: 14,
                      height: 14
                    }}
                  />
                )}
                <Text variant="body3" color={item.isUsed ? colorSet.white : colorSet.gray600}>
                  {item.usedCount ? item.usedCount.toLocaleString() : 0}
                </Text>
              </UseButton>
              <BookmarkButton
                className="hover-visible"
                isCollected={item.isCollected}
                onClick={(event) => {
                  event.stopPropagation(); // 이벤트 전파 방지
                  handleCollectionSelectModalOpen();
                }}
              >
                {item.isCollected ? (
                  <BookmarkWhiteIcon
                    style={{
                      width: 14,
                      height: 14
                    }}
                  />
                ) : (
                  <BookmarkGray600Icon
                    style={{
                      width: 14,
                      height: 14
                    }}
                  />
                )}
              </BookmarkButton>
            </div>
            <DownloadButton
              className="hover-visible"
              onClick={(event) => {
                event.stopPropagation(); // 이벤트 전파 방지
                downloadImage(item.s3Url, item.originFileName).then((isSuccess) => {
                  if (isSuccess) {
                    showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
                  }
                });
              }}
            >
              <DownloadWhiteIcon style={{ width: 14, height: 14 }} />
            </DownloadButton>
          </ButtonsContainer>
        ) : null}
      </Container>

      <CollectionSelectModal
        isVisible={isCollectionSelectModalVisible}
        onClose={handleCollectionSelectModalClose}
        imageId={item.id}
        onCollectionUpsertModalOpen={handleCollectionUpsertModalOpen}
        createQueryStringForImageList={createQueryStringForImageList}
      />

      <CollectionUpsertModal
        isVisible={isCollectionUpsertModalVisible}
        showGoBackButton={showGoBackButton}
        onClose={handleCollectionUpsertModalClose}
        onGoBackButtonClick={handleGoBackToCollectionSelectModal}
      />
    </>
  );
};
